import React, { useRef, useState } from 'react'
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, FormControl, Typography, Checkbox, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import stylesheet from '../detail-container.stylesheet'
import { ClassNameMap, withStyles } from '@mui/styles'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import { getMetaData } from '../../../../redux/meta-data-slice'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import Draggable from 'react-draggable'

interface IProps {
  open: boolean
  onClose: () => void
  classes: ClassNameMap
}

const ExportPatientsDialogComponent: React.FC<IProps> = ({ open, onClose, classes }) => {
  const patientService = new PatientService()
  const dispatch = useAppThunkDispatch()
  const [loading, setLoading] = useState(false)
  const [selectedHospitals, setSelectedHospitals] = useState<any[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dialogRef = useRef(null) as any
  const hospitals = useSelector((state: RootState) => state.metaData.metadata.HOSPITALS)

  React.useEffect(() => {
    if (!hospitals || Object.keys(hospitals).length === 0) {
      dispatch(getMetaData())
    }
  }, [])

  const hospitalOptions = hospitals
    ? Object.keys(hospitals)
        .map((key) => ({ id: key, label: hospitals[key] }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : []

  const isSubmitDisabled = loading || selectedHospitals.length === 0

  const handleSubmit = () => {
    if (isSubmitDisabled) return
    setLoading(true)
    patientService
      .exportPatients(selectedHospitals.map((h: any) => h.id))
      .then((res: any) => {
        //   Helper.handleTaskReportFile(res?.data, 'hospital_export.zip')
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.href = url

        // Extract filename from Content-Disposition header, or fall back to default
        const disposition = res.headers['content-disposition']
        const filename = disposition ? disposition.split('filename=')[1]?.replace(/['"]/g, '') : `hospital_patients_${Date.now()}.csv`

        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Export started successfully', Helper.bottom_center())
        onClose()
      })
      .catch((err: any) => {
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
      })
      .finally(() => setLoading(false))
  }

  return (
    <Draggable ref={dialogRef} handle=".export-patients-dialog">
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={onClose}
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          if (e.key === 'Enter' && !isSubmitDisabled && !dropdownOpen) handleSubmit()
        }}
        sx={{ position: 'absolute', left: '15%', top: '15%' }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'export-patients-dialog' }}>
          <DialogTitleHeaderComponent title="Export patients" handleDialogClose={onClose} />
        </DialogTitle>
        <Grid item>{loading && <LinearProgress />}</Grid>
        <DialogContent className={classes.addPatientDialogContent}>
          <Grid container spacing={1}>
            <Grid item md={12} xs={12}>
              <FormControl className={classes.formControl}>
                <Typography variant="subtitle1" className={classes.desc}>
                  Select hospitals
                </Typography>
                <Autocomplete
                  multiple
                  id="export-hospitals"
                  options={hospitalOptions}
                  getOptionLabel={(option: any) => option.label}
                  disablePortal={false}
                  className={classes.chipSearch}
                  onOpen={() => setDropdownOpen(true)}
                  onClose={() => setDropdownOpen(false)}
                  renderOption={(props, option, { selected }) => (
                    <Typography {...props} sx={{ padding: '0px !important', fontSize: '0.65rem !important' }} className={classes.desc}>
                      <Checkbox
                        sx={{ padding: '0.2rem 0.3rem !important', transform: 'scale(0.6)' }}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        checked={selected}
                      />
                      {option.label}
                    </Typography>
                  )}
                  renderInput={(params: any) => <TextField {...params} variant="standard" placeholder="" />}
                  onChange={(_e, newValue) => setSelectedHospitals(newValue)}
                  sx={{
                    minWidth: '250px',
                    '& .MuiChip-root': { height: '1rem !important', fontSize: '0.65rem !important' },
                    '& .MuiSvgIcon-root': { fontSize: '0.8rem !important' },
                    '&.MuiAutocomplete-option': { minHeight: 'auto !important', padding: '0px 6px !important' },
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
            <Grid item>
              <Button variant="outlined" size="small" disabled={isSubmitDisabled} onClick={handleSubmit} className={isSubmitDisabled ? classes.disabledBtn : ''}>
                Submit
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" disabled={loading} onClick={onClose} className={loading ? classes.disabledBtn : ''}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Draggable>
  )
}

export default withStyles(stylesheet)(ExportPatientsDialogComponent)
